import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { gql } from '@apollo/client';
import apolloClient from '../config/apolloClient';

// Configura Firebase con tus credenciales
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

// Inicializa Firebase solo si está configurado
let auth: any = null;
try {
  if (firebaseConfig.apiKey && firebaseConfig.apiKey !== 'your-api-key-here') {
    const app = initializeApp(firebaseConfig);
    auth = getAuth(app);
  }
} catch (error: any) {
  console.warn('Firebase no está configurado correctamente:', error.message || error);
  auth = null;
}

// Mutations GraphQL para sincronizar con tu backend
const CREATE_USER = gql`
  mutation CreateUser($email: String!, $name: String!, $firebaseUid: String!) {
    createUser(email: $email, name: $name, firebaseUid: $firebaseUid) {
      id
      email
      name
    }
  }
`;

const LOGIN_USER = gql`
  mutation LoginUser($email: String!, $firebaseUid: String!) {
    loginUser(email: $email, firebaseUid: $firebaseUid) {
      token
      user {
        id
        email
        name
      }
    }
  }
`;

interface AuthContextType {
  currentUser: FirebaseUser | null;
  loading: boolean;
  isPremium: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium] = useState(false);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Obtén el token de Firebase y guárdalo
        const token = await user.getIdToken();
        localStorage.setItem('authToken', token);
      } else {
        localStorage.removeItem('authToken');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    if (!auth) {
      throw new Error('Firebase no está configurado. Por favor configura las credenciales de Firebase.');
    }

    try {
      // Registra en Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Actualiza el perfil con el nombre
      await updateProfile(user, { displayName: name });

      // Sincroniza con tu backend GraphQL
      await apolloClient.mutate({
        mutation: CREATE_USER,
        variables: {
          email: user.email,
          name: name,
          firebaseUid: user.uid,
        },
      });

      // Obtén y guarda el token
      const token = await user.getIdToken();
      localStorage.setItem('authToken', token);
    } catch (error: any) {
      console.error('Error en registro:', error);
      throw new Error(error.message || 'Error al registrarse');
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (!auth) {
      throw new Error('Firebase no está configurado. Por favor configura las credenciales de Firebase.');
    }

    try {
      // Inicia sesión en Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Obtén un token fresco de Firebase
      const token = await user.getIdToken(true); // true para forzar refresh
      localStorage.setItem('authToken', token);
      
      console.log('✅ Login exitoso, token guardado');

      // Opcional: Sincroniza con tu backend
      try {
        await apolloClient.mutate({
          mutation: LOGIN_USER,
          variables: {
            email: user.email,
            firebaseUid: user.uid,
          },
        });
      } catch (err) {
        console.warn('Error al sincronizar con backend, pero login exitoso:', err);
      }
    } catch (error: any) {
      console.error('Error en login:', error);
      throw new Error(error.message || 'Error al iniciar sesión');
    }
  }, []);

  const logout = useCallback(async () => {
    if (!auth) {
      localStorage.removeItem('authToken');
      return;
    }

    try {
      await signOut(auth);
      localStorage.removeItem('authToken');
    } catch (error: any) {
      console.error('Error en logout:', error);
      throw new Error(error.message || 'Error al cerrar sesión');
    }
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (currentUser) {
      return await currentUser.getIdToken();
    }
    return null;
  }, [currentUser]);

  const value = useMemo<AuthContextType>(() => ({
    currentUser,
    loading,
    isPremium,
    login,
    register,
    logout,
    getToken,
  }), [currentUser, loading, isPremium, login, register, logout, getToken]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
